import { AlertCircle, Check, FileText, Vote, type LucideIcon } from 'lucide-react';
import { assertNever } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import type { KeyDateStatus, KeyDateItem } from '@/lib/keyDates';

// The left border color is an inline style because the unlayered `* { border-color }` rule in index.css
// outranks Tailwind's layered border-color utilities.
function statusStyle(status: KeyDateStatus): { className: string; borderColor: string; Icon: LucideIcon; spoken: string } {
  switch (status) {
    case 'election':
      return {
        className: 'bg-calendar-election/10 text-calendar-election',
        borderColor: 'var(--color-calendar-election)',
        Icon: Vote,
        spoken: 'election day',
      };
    case 'upcoming':
      return {
        className: 'bg-calendar-deadline/10 text-calendar-deadline',
        borderColor: 'var(--color-calendar-deadline)',
        Icon: FileText,
        spoken: 'filing deadline',
      };
    case 'overdue':
      return {
        className: 'bg-calendar-overdue text-white',
        borderColor: 'var(--color-calendar-overdue)',
        Icon: AlertCircle,
        spoken: 'overdue',
      };
    case 'filed':
      return {
        className: 'bg-muted text-calendar-filed',
        borderColor: 'var(--color-muted-foreground)',
        Icon: Check,
        spoken: 'filed',
      };
    default:
      return assertNever(status);
  }
}

interface KeyDateChipProps {
  item: KeyDateItem;
  className?: string;
}

export function KeyDateChip({ item, className }: KeyDateChipProps) {
  const { className: statusClassName, borderColor, Icon, spoken } = statusStyle(item.status);
  const name = `${item.label}, ${spoken}`;

  return (
    <div
      role="listitem"
      aria-label={name}
      title={name}
      className={cn(
        'flex items-center gap-1 rounded-r border-l-[3px] px-1.5 py-0.5 leading-tight font-medium cursor-default',
        statusClassName,
        className
      )}
      style={{ borderLeftColor: borderColor }}
    >
      <Icon className="h-2.5 w-2.5 shrink-0" aria-hidden />
      <span className="truncate">{item.label}</span>
    </div>
  );
}
